// Adım 4 — Aktar (M16 T11; mockup 07). Sonuç sayıları (all-or-nothing güvencesi metinde),
// kural 8 yıl aralığı notu, D10 ✓-reset notu, CTA'lar: Veri Girişi / Özet / yeni dosya.

import { Link } from "react-router-dom";
import type { KpiImportCommitResult, KpiImportJobDto } from "../../../api/kpi-import";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import { btnGhost, btnPrimary } from "../kpi-form-styles";

export interface ImportCommitStepProps {
  job: KpiImportJobDto;
  result: KpiImportCommitResult;
  committedAt: string;
  yearsNote: { outside: number[]; view: number[] } | null;
  onNewFile: () => void;
  /** bağlantı kökü (iç `/clients/kpi`, portal `/portal/kpi`) */
  basePath?: string;
}

const stat: React.CSSProperties = { display: "flex", flexDirection: "column", minWidth: 120 };
const num: React.CSSProperties = {
  fontSize: "1.5rem",
  fontWeight: 700,
  color: "var(--color-primary)",
  lineHeight: 1.2,
};
const note: React.CSSProperties = {
  border: "1px solid var(--color-border)",
  borderRadius: 12,
  padding: "10px 14px",
  fontSize: "0.9rem",
  color: "var(--color-text-2)",
  background: "var(--color-surface)",
};

export function ImportCommitStep({
  job,
  result,
  committedAt,
  yearsNote,
  onNewFile,
  basePath = "/clients/kpi",
}: ImportCommitStepProps): JSX.Element {
  const c = KPI_IMPORT_COPY.commit;
  const written = result.created + result.updated;
  const stats: [string, number, string][] = [
    ["created", result.created, c.stats.created],
    ["updated", result.updated, c.stats.updated],
    ["conflictsKept", result.conflictsKept, c.stats.conflictsKept],
    ["skipped", result.skipped, c.stats.skipped],
    ["newIndicators", result.newIndicators, c.stats.newIndicators],
  ];
  // spec §7.2 — kırılım üyesi olarak sete eklenen satırlar (yalnız varsa; portalda hep 0)
  const bdMembers = result.newBreakdownMembers ?? 0;
  if (bdMembers > 0)
    stats.push(["newBreakdownMembers", bdMembers, KPI_IMPORT_COPY.breakdown.commitStat]);
  return (
    <div className="flex flex-col gap-3">
      <div
        role="status"
        className="rounded-xl p-5"
        style={{
          border: "1px solid var(--color-primary)",
          background: "color-mix(in srgb, var(--color-primary) 6%, white)",
        }}
      >
        <div
          className="font-semibold"
          style={{ fontSize: "1.15rem", color: "var(--color-primary)" }}
        >
          ✓ {c.title}
        </div>
        <div className="text-sm mt-1 mb-3" style={{ color: "var(--color-text-2)" }}>
          {c.body(written)}
        </div>
        <div className="flex gap-6 flex-wrap">
          {stats.map(([k, v, label]) => (
            <div key={k} style={stat}>
              <span style={num}>{v}</span>
              <span className="text-sm" style={{ color: "var(--color-text-2)" }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {yearsNote && yearsNote.outside.length > 0 ? (
        <div style={note}>
          ℹ{" "}
          {c.yearsNote(
            Math.min(...yearsNote.outside),
            Math.max(...yearsNote.outside),
            yearsNote.view[0] ?? 0,
            yearsNote.view[yearsNote.view.length - 1] ?? 0,
          )}
        </div>
      ) : null}
      {result.updated > 0 ? <div style={note}>ℹ {c.checkNote}</div> : null}
      {result.accessSkipped.length > 0 ? (
        <div style={note} data-testid="kpi-import-commit-access-skipped">
          ℹ {c.accessSkipped(result.accessSkipped.length)}{" "}
          {result.accessSkipped.map((r) => r.rowLabel).join(", ")}
        </div>
      ) : null}

      <div className="flex items-center gap-3 flex-wrap mt-2">
        <Link
          to={`${basePath}/tablo`}
          style={{
            ...btnPrimary,
            display: "inline-flex",
            alignItems: "center",
            textDecoration: "none",
          }}
        >
          {c.goTable}
        </Link>
        <Link
          to={`${basePath}/ozet`}
          style={{
            ...btnGhost,
            display: "inline-flex",
            alignItems: "center",
            textDecoration: "none",
          }}
        >
          {c.goSummary}
        </Link>
        <button
          type="button"
          style={{ ...btnGhost, border: "none", color: "var(--color-primary)", fontWeight: 600 }}
          onClick={onNewFile}
        >
          {c.newFile}
        </button>
      </div>
      <div style={{ fontSize: "0.78rem", color: "var(--color-text-3)" }}>
        {c.record(new Date(committedAt).toLocaleString("tr-TR"), job.fileName)}
      </div>
    </div>
  );
}
